import {Anchor, Text} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React from 'react';

import HelpButton from 'help/HelpButton';
import SchemaFieldValueErrorsModal from 'metadata/SchemaFieldValueErrorsModal';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';

interface SchemaFieldValueErrorProps {
  errorLabel: string;
  schemaFieldRenderData: SchemaFieldConstants.SchemaFieldRenderData;
  messages: Array<string>;
}

// Keep in sync with benchling/schemas/validation/base.py.
export const REQUIRED_FIELD_ERROR_LABEL = 'Required value';

/*
 * Rewrite some messages to be more useful to the user. Otherwise, use the message included.
 */
function getErrorMessageForMetadataTab(
  errorLabel: string,
  schemaFieldRenderData: SchemaFieldConstants.SchemaFieldRenderData,
  message: string
): string {
  if (
    schemaFieldRenderData.fieldType === SchemaFieldConstants.FIELD_TYPES.PART_LINK.type &&
    errorLabel === REQUIRED_FIELD_ERROR_LABEL
  ) {
    return `Highlight bases in the Sequence Map viewer, right-click, and select
    “Create new part... > ${schemaFieldRenderData.name}"`;
  }
  return message;
}

/**
 * Renders an error (e.g. Registry Validation errors in the Metadata tab).
 * Shows a red error label and a Help button, which shows a popover on click. When the field has a single
 * error message, the popover shows the full message. When it has multiple messages for the same label, the
 * popover summarizes and links to a modal listing every message.
 */
export default function SchemaFieldValueError(props: SchemaFieldValueErrorProps): JSX.Element {
  const {errorLabel, schemaFieldRenderData, messages} = props;
  const [modalOpened, modal] = useDisclosure(false);
  const displayMessages = messages.map((message) =>
    getErrorMessageForMetadataTab(errorLabel, schemaFieldRenderData, message)
  );
  const hasMultiple = displayMessages.length > 1;
  return (
    <div
      className="metadata-fieldError"
      {...getTestProps({component: 'SchemaFieldValueError', element: 'root'})}
    >
      <span
        className="u-danger-color"
        {...getTestProps({component: 'SchemaFieldValueError', element: 'label'})}
      >
        {errorLabel}
      </span>
      <HelpButton
        renderContent={() =>
          hasMultiple ? (
            <Text size="sm">
              This field has {displayMessages.length} “{errorLabel}” errors.{' '}
              <Anchor
                component="button"
                type="button"
                size="sm"
                onClick={modal.open}
                {...getTestProps({component: 'SchemaFieldValueError', element: 'viewAll'})}
              >
                View all errors
              </Anchor>
            </Text>
          ) : (
            displayMessages[0]
          )
        }
        iconClassName="u-mini-spacing-left"
        placement={FloatingPlacement.BOTTOM}
      />
      {hasMultiple && (
        <SchemaFieldValueErrorsModal
          opened={modalOpened}
          onClose={modal.close}
          errorLabel={errorLabel}
          messages={displayMessages}
        />
      )}
    </div>
  );
}
